import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addRFIComment,
  assignRFI,
  closeRFI,
  createRFI,
  deleteRFI,
  deleteRFIAttachment,
  getRFIById,
  getRFIs,
  getRFIStatusSummary,
  getRFIWidgetCounts,
  getProjectShareableUsers,
  getAreaTagsByProject,
  getProjectLayouts,
  createAreaTagForProject,
  getProjectDrawingSheets,
  reopenRFI,
  respondToRFI,
  submitRFI,
  updateRFI,
  uploadRFIAttachment,
  linkRFIAttachmentPhoto,
} from '../api/api';
import type { CreateRFIData, RFIFilters, UpdateRFIData } from '../types';

export const rfiKeys = {
  all: ['rfis'] as const,
  lists: (projectId: number) => [...rfiKeys.all, 'list', projectId] as const,
  list: (projectId: number, filters: RFIFilters) =>
    [...rfiKeys.lists(projectId), filters] as const,
  detail: (id: string) => [...rfiKeys.all, 'detail', id] as const,
  statusSummary: (projectId: number) => [...rfiKeys.all, 'status-summary', projectId] as const,
  widgetCounts: (projectId: number) => [...rfiKeys.all, 'widget-counts', projectId] as const,
  areaTags: (projectId: number) => [...rfiKeys.all, 'area-tags', projectId] as const,
  layouts: (projectId: number) => [...rfiKeys.all, 'layouts', projectId] as const,
  drawingSheets: (projectId: number) => [...rfiKeys.all, 'drawing-sheets', projectId] as const,
};

export const useRFIs = (projectId: number | undefined, filters: RFIFilters = {}) =>
  useQuery({
    queryKey: rfiKeys.list(projectId!, filters),
    queryFn: () => getRFIs(projectId!, filters),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useRFI = (id: string | undefined, projectId: number | undefined) =>
  useQuery({
    queryKey: rfiKeys.detail(id!),
    queryFn: () => getRFIById(id!, projectId!),
    enabled: !!id && !!projectId,
    staleTime: 60_000,
  });

export const useRFIStatusSummary = (projectId: number | undefined) =>
  useQuery({
    queryKey: rfiKeys.statusSummary(projectId!),
    queryFn: () => getRFIStatusSummary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useRFIWidgetCounts = (projectId: number | undefined) =>
  useQuery({
    queryKey: rfiKeys.widgetCounts(projectId!),
    queryFn: () => getRFIWidgetCounts(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useProjectUsers = (projectId: number | undefined) =>
  useQuery({
    queryKey: [...rfiKeys.all, 'project-users', projectId] as const,
    queryFn: () => getProjectShareableUsers(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useProjectAreaTags = (projectId: number | undefined) =>
  useQuery({
    queryKey: rfiKeys.areaTags(projectId!),
    queryFn: () => getAreaTagsByProject(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useProjectLayouts = (projectId: number | undefined) =>
  useQuery({
    queryKey: rfiKeys.layouts(projectId!),
    queryFn: () => getProjectLayouts(projectId!),
    enabled: !!projectId,
    staleTime: 10 * 60_000,
  });

export const useCreateAreaTag = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; layout_id: number }) =>
      createAreaTagForProject({ ...payload, project_id: projectId! }),
    onSuccess: () => {
      if (!projectId) return;
      queryClient.invalidateQueries({ queryKey: rfiKeys.areaTags(projectId) });
    },
  });
};

export const useProjectDrawingSheets = (projectId: number | undefined) =>
  useQuery({
    queryKey: rfiKeys.drawingSheets(projectId!),
    queryFn: () => getProjectDrawingSheets(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useRFIMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  const invalidateProject = () => {
    if (!projectId) return;
    queryClient.invalidateQueries({ queryKey: rfiKeys.lists(projectId) });
    queryClient.invalidateQueries({ queryKey: rfiKeys.statusSummary(projectId) });
    queryClient.invalidateQueries({ queryKey: rfiKeys.widgetCounts(projectId) });
  };

  const createItem = useMutation({
    mutationFn: (data: CreateRFIData) => createRFI(data),
    onSuccess: () => invalidateProject(),
  });

  const updateItem = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRFIData }) => updateRFI(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: rfiKeys.detail(result.id) });
      invalidateProject();
    },
  });

  const deleteItem = useMutation({
    mutationFn: (id: string) => deleteRFI(id),
    onSuccess: () => invalidateProject(),
  });

  const refreshDetail = (id: string) => {
    queryClient.invalidateQueries({ queryKey: rfiKeys.detail(id) });
    invalidateProject();
  };

  const submitItem = useMutation({
    mutationFn: (id: string) => submitRFI(id),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const assignItem = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { responsible_party_user_id?: number; assignee_user_id?: number };
    }) => assignRFI(id, payload),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const respondItem = useMutation({
    mutationFn: ({ id, official_response }: { id: string; official_response: string }) =>
      respondToRFI(id, official_response),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const closeItem = useMutation({
    mutationFn: ({ id, official_response }: { id: string; official_response?: string }) =>
      closeRFI(id, official_response),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const reopenItem = useMutation({
    mutationFn: (id: string) => reopenRFI(id),
    onSuccess: (result) => refreshDetail(result.id),
  });

  const addComment = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => addRFIComment(id, body),
    onSuccess: (_data, { id }) => refreshDetail(id),
  });

  const uploadAttachment = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadRFIAttachment(id, file),
    onSuccess: (_data, { id }) => refreshDetail(id),
  });

  const linkAttachmentPhoto = useMutation({
    mutationFn: ({ id, photoId }: { id: string; photoId: number }) =>
      linkRFIAttachmentPhoto(id, photoId),
    onSuccess: (_data, { id }) => refreshDetail(id),
  });

  const deleteAttachment = useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      deleteRFIAttachment(attachmentId).then(() => id),
    onSuccess: (id) => refreshDetail(id),
  });

  return {
    createItem,
    updateItem,
    deleteItem,
    submitItem,
    assignItem,
    respondItem,
    closeItem,
    reopenItem,
    addComment,
    uploadAttachment,
    linkAttachmentPhoto,
    deleteAttachment,
  };
};
